'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';

export interface RoleGateProps {
  allowedUserTypes: readonly string[];
  children: React.ReactNode;
}

type AuthState = 'checking' | 'unauthenticated' | 'forbidden' | 'allowed';

function normalize(code: string | null | undefined): string {
  return (code ?? '').trim().toUpperCase();
}

function readAuthFromStorage(): { token: string; userType: string } | null {
  if (typeof window === 'undefined') return null;
  const token =
    window.localStorage.getItem('super_admin_token') ||
    window.localStorage.getItem('access_token') ||
    '';
  const userType = window.localStorage.getItem('user_type') ?? '';
  if (!token) return null;
  return { token, userType };
}

export function RoleGate({ allowedUserTypes, children }: RoleGateProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [state, setState] = useState<AuthState>('checking');

  useEffect(() => {
    const auth = readAuthFromStorage();

    if (!auth) {
      setState('unauthenticated');
      const redirectParam = pathname ? `?redirect=${encodeURIComponent(pathname)}` : '';
      router.replace(`/login${redirectParam}`);
      return;
    }

    const normalizedAllowed = allowedUserTypes.map(normalize);
    if (normalizedAllowed.includes(normalize(auth.userType))) {
      setState('allowed');
    } else {
      setState('forbidden');
      router.replace('/unauthorized');
    }
  }, [allowedUserTypes, pathname, router]);

  if (state !== 'allowed') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-[40vh] items-center justify-center bg-background"
      >
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
          <p className="mt-3 text-sm text-gray-500">
            {state === 'checking' && 'Checking access…'}
            {state === 'unauthenticated' && 'Redirecting to sign in…'}
            {state === 'forbidden' && 'Not authorized.'}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export const PRE_INSPECTION_ALLOWED_USER_TYPES = [
  'SUPER_ADMIN',
  'PLATFORM_ADMIN',
  'CLAIMS_ADJUSTER',
  'VERIFIER',
  'INSURER_USER',
  'INSURER_ADMIN',
] as const;
